/**
 * tickerNavigation — pure helpers behind the game and injury tickers' league
 * skip buttons (Oct 2026 rebuild).
 *
 * The bug this replaces: GameTicker measured row offsets with onLayout and
 * reset them whenever the fixture set changed; react-native-web only fires
 * onLayout on mount/resize, so retained rows never re-reported and the ticker
 * jumped to estimated offsets (NFL > landed mid-NBA). Here the labels and the
 * jump targets come from ONE section list, and offsets are read from the DOM
 * when they are needed.
 *
 * Coordinates: `offset` is the distance the strip has scrolled left, in
 * [0, loopWidth). The strip renders translateX = -offset over 2+ identical
 * copies, so any offset in that range shows a full row of games.
 */

/** Major leagues first; anything unknown sorts after them, alphabetically. */
export const LEAGUE_PRIORITY = {
    nba: 1, nfl: 2, nhl: 3, mlb: 4,
    ncaab: 5, ncaaf: 6, wnba: 7,
    epl: 8, mls: 9, champions_league: 10,
    ufc: 11, atp: 12, wta: 13,
};

export const leagueKey = (league) => String(league || '').trim().toLowerCase();

/**
 * Orders two league ids. Unknown leagues used to share priority 99 and fall
 * back to time order, which interleaved them into several sections.
 */
export function compareLeagues(a, b) {
    const ka = leagueKey(a);
    const kb = leagueKey(b);
    if (ka === kb) return 0;
    const pa = LEAGUE_PRIORITY[ka] ?? 99;
    const pb = LEAGUE_PRIORITY[kb] ?? 99;
    if (pa !== pb) return pa - pb;
    // Empty league last, then alphabetical so each league stays contiguous.
    if (!ka) return 1;
    if (!kb) return -1;
    return ka < kb ? -1 : 1;
}

/**
 * Stable sort by league (compareLeagues), then by `tieBreak` within a league.
 * Never mutates the input.
 */
export function sortByLeague(items, getLeague, tieBreak) {
    return (items || [])
        .map((item, i) => ({ item, i }))
        .sort((x, y) => {
            const byLeague = compareLeagues(getLeague(x.item), getLeague(y.item));
            if (byLeague !== 0) return byLeague;
            const t = tieBreak ? tieBreak(x.item, y.item) : 0;
            return t !== 0 ? t : x.i - y.i;
        })
        .map((w) => w.item);
}

/**
 * Contiguous league runs: [{ league, startIndex, count }]. On a list sorted by
 * sortByLeague there is exactly one section per league.
 */
export function buildLeagueSections(items, getLeague) {
    const sections = [];
    (items || []).forEach((item, index) => {
        const league = leagueKey(getLeague(item));
        const last = sections[sections.length - 1];
        if (last && last.league === league) last.count += 1;
        else sections.push({ league, startIndex: index, count: 1 });
    });
    return sections;
}

/** Section after/before `index` on a loop; -1 when there is nowhere to go. */
export function adjacentSectionIndex(sections, index, direction) {
    const n = sections ? sections.length : 0;
    if (n < 2 || index < 0 || index >= n) return -1;
    return direction === 'prev' ? (index - 1 + n) % n : (index + 1) % n;
}

/** Section containing item `itemIndex` (by startIndex/count). */
export function sectionIndexForItem(sections, itemIndex) {
    for (let i = (sections || []).length - 1; i >= 0; i--) {
        if (itemIndex >= sections[i].startIndex) return i;
    }
    return 0;
}

/** Wraps any offset into [0, width). */
export function wrapOffset(offset, width) {
    if (!(width > 0) || !Number.isFinite(offset)) return 0;
    const r = offset % width;
    return r < 0 ? r + width : r;
}

/**
 * Which section is at the left edge for a given offset. A half-pixel slack
 * keeps a jump that lands exactly on a section start inside that section.
 */
export function sectionIndexAtOffset(offset, sectionOffsets, width) {
    const n = sectionOffsets ? sectionOffsets.length : 0;
    if (n === 0) return 0;
    const pos = wrapOffset(offset, width) + 0.5;
    let found = -1;
    for (let i = 0; i < n; i++) {
        if (sectionOffsets[i] <= pos) found = i;
    }
    // Before the first section start (only when section 0 is not at 0): we are
    // still in the last section of the previous copy.
    return found === -1 ? n - 1 : found;
}

/**
 * Reads row geometry from DOM nodes at call time (web). `nodes` are the row
 * elements in order, including at least the first row of the second copy.
 * Returns { rowOffsets, loopWidth } or null when the nodes are not laid out.
 */
export function measureRowOffsets(nodes, rowCount) {
    if (!nodes || rowCount <= 0 || nodes.length < rowCount) return null;
    const base = nodes[0].offsetLeft || 0;
    const rowOffsets = [];
    for (let i = 0; i < rowCount; i++) {
        const node = nodes[i];
        if (!node || !(node.offsetWidth > 0)) return null;
        rowOffsets.push(node.offsetLeft - base);
    }
    const seam = nodes[rowCount];
    const last = nodes[rowCount - 1];
    const loopWidth = seam && seam.offsetWidth > 0
        ? seam.offsetLeft - base
        : last.offsetLeft - base + last.offsetWidth;
    if (!(loopWidth > 0)) return null;
    return { rowOffsets, loopWidth };
}

/** Row whose span contains `offset` (rowOffsets ascending, wrapping). */
export function rowIndexAtOffset(offset, rowOffsets, width) {
    const pos = wrapOffset(offset, width);
    let found = 0;
    for (let i = 0; i < rowOffsets.length; i++) {
        if (rowOffsets[i] <= pos + 0.5) found = i;
        else break;
    }
    return found;
}

/**
 * Keeps the same game at the left edge when geometry changes (fonts load, a
 * 5-minute refresh drops or adds games). Falls back to a plain wrap when that
 * game is gone.
 */
export function reanchorOffset(offset, prev, next) {
    if (!next || !(next.loopWidth > 0)) return offset;
    if (!prev || !(prev.loopWidth > 0) || !prev.rowOffsets?.length) return wrapOffset(offset, next.loopWidth);
    const pos = wrapOffset(offset, prev.loopWidth);
    const row = rowIndexAtOffset(pos, prev.rowOffsets, prev.loopWidth);
    const id = prev.ids?.[row];
    const nextRow = id != null && next.ids ? next.ids.indexOf(id) : -1;
    if (nextRow < 0) return wrapOffset(pos, next.loopWidth);
    const within = pos - prev.rowOffsets[row];
    return wrapOffset(next.rowOffsets[nextRow] + within, next.loopWidth);
}

/** Default glide feel; the planner caps distance so long jumps don't blur. */
export const JUMP = {
    maxGlide: 1400,     // px actually animated; anything further snaps first
    minDuration: 380,
    maxDuration: 720,
    msPerPx: 0.11,
};

/**
 * Plans a league jump in UNWRAPPED coordinates.
 *   - Always moves in the button's direction ('next' = forward/left,
 *     'prev' = backward) even across the loop seam.
 *   - The caller renders wrapOffset(value), so no blank space can show.
 *   - Glides at most `maxGlide` px: farther targets snap to a point that far
 *     away first.
 *   - Reduced motion: duration 0 (instant).
 * Returns { from, to, duration } with to ≡ target (mod width).
 */
export function planLeagueJump(current, target, width, direction, opts = {}) {
    const cfg = { ...JUMP, ...opts };
    if (!(width > 0)) return { from: current, to: current, duration: 0 };
    const cur = wrapOffset(current, width);
    const tgt = wrapOffset(target, width);
    let delta = direction === 'prev'
        ? -wrapOffset(cur - tgt, width)
        : wrapOffset(tgt - cur, width);
    // Already there (or within a pixel of the seam): land on it, no lap.
    if (Math.abs(delta) < 1 || Math.abs(delta) > width - 1) delta = 0;
    const to = cur + delta;
    if (cfg.reduced || delta === 0) return { from: tgt, to: tgt, duration: 0 };
    const dist = Math.abs(delta);
    const glide = Math.min(dist, cfg.maxGlide);
    const from = to - Math.sign(delta) * glide;
    const duration = Math.round(Math.min(cfg.maxDuration, Math.max(cfg.minDuration, cfg.minDuration + glide * cfg.msPerPx)));
    return { from, to, duration };
}

/** easeOutCubic-ish quint: fast start, soft landing on the target section. */
export function easeOutQuint(t) {
    const x = Math.min(1, Math.max(0, t));
    return 1 - Math.pow(1 - x, 5);
}

/** Copies of the strip needed so the viewport never runs out of games. */
export function copiesNeeded(viewportWidth, loopWidth) {
    if (!(loopWidth > 0) || !(viewportWidth > 0)) return 2;
    return Math.min(6, Math.max(2, Math.ceil(viewportWidth / loopWidth) + 1));
}
